import { isQuoteDefinitionBound } from './businessFieldDirectory';
import { updateQuote } from './machine';
import {
  isQuoteFieldAvailable,
  quoteFieldAvailabilityForQuote,
} from './quoteFieldAvailability';
import {
  assertQuoteFieldSelectionAllowed,
  canSelectGovernedQuoteField,
  mergeRawQuoteColumnSnapshot,
  mergeRawQuoteFieldKeys,
} from './quoteFieldPolicy';
import { captureQuoteSourceFieldSnapshots } from './quoteFieldSnapshots';
import type {
  CpqState,
  QuoteColumnSnapshot,
  QuoteFieldDefinitionRecord,
  QuoteFieldPlacement,
  QuoteFieldSelectionRecord,
} from './types';
import { createDisposableFieldValuePort } from './unifiedFieldPort';
import {
  type UnifiedFieldBinding,
  unifiedFieldBindingForKey,
} from './unifiedFieldRegistry';

function toQuoteColumnSnapshot(
  field: QuoteFieldDefinitionRecord,
  placement: QuoteFieldPlacement,
): QuoteColumnSnapshot {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    width: field.width,
    note: field.note,
    options: field.options?.map((option) => ({ ...option })),
    sourceKind: field.sourceKind,
    ...(field.calculation
      ? { calculation: structuredClone(field.calculation) }
      : {}),
    placement,
  };
}

export function quoteFieldSelections(
  quote: Pick<import('./types').Quote, 'customColumns' | 'fieldSelections'>,
): QuoteFieldSelectionRecord[] {
  const nonLine = (quote.fieldSelections ?? []).filter(
    (selection) => selection.placement !== 'quote-line',
  );
  return [
    ...quote.customColumns.map((fieldKey) => ({
      fieldKey,
      placement: 'quote-line' as const,
    })),
    ...nonLine,
  ];
}

export function quoteFieldKeysForPlacement(
  quote: Pick<import('./types').Quote, 'customColumns' | 'fieldSelections'>,
  placement: QuoteFieldPlacement,
): string[] {
  return quoteFieldSelections(quote)
    .filter((selection) => selection.placement === placement)
    .map((selection) => selection.fieldKey);
}

/**
 * The sole state transition for custom-field selection. Validation occurs
 * before updateQuote clones state, so a denied injection is a zero-write.
 */
export function applyQuoteFieldSelection(
  state: CpqState,
  quoteId: string,
  requestedKeys: string[],
  fieldValuePort = createDisposableFieldValuePort(),
): CpqState {
  const quote = state.quotes[quoteId];
  if (!quote) throw new Error(`报价 ${quoteId} 不存在`);
  const configured = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  if (new Set(requestedKeys).size !== requestedKeys.length) {
    throw new Error('字段选择重复，未保存本次字段选择');
  }
  const selectablePool = state.catalog.quoteFieldPool
    .filter((entry) => {
      let binding: UnifiedFieldBinding | undefined;
      try {
        binding = unifiedFieldBindingForKey(
          state.catalog.quoteFieldDefinitions,
          entry.fieldKey,
        );
      } catch {
        binding = undefined;
      }
      return (
        entry.enabled &&
        isQuoteDefinitionBound(entry.fieldKey) &&
        binding?.poolAllowed === true &&
        binding.quotePlacement === entry.placement
      );
    })
    .sort((left, right) => left.sortOrder - right.sortOrder);
  const poolKeys = new Set(selectablePool.map((entry) => entry.fieldKey));
  if (
    requestedKeys.some((key) => {
      const field = configured.get(key);
      return !field?.enabled || field.governance.classification !== 'ordinary';
    })
  ) {
    throw new Error('字段治理状态不足，未保存本次字段选择');
  }
  if (requestedKeys.some((key) => !poolKeys.has(key))) {
    throw new Error('字段不在报价字段池，未保存本次字段选择');
  }
  if (
    requestedKeys.some(
      (key) =>
        quoteFieldAvailabilityForQuote(
          key,
          quote,
          state.catalog.quoteFieldDefinitions,
        ) !== 'available',
    )
  ) {
    throw new Error('字段当前报价不可用，未保存本次字段选择');
  }
  const mandatoryKeys = new Set(
    selectablePool
      .filter((entry) => entry.mandatory)
      .map((entry) => entry.fieldKey),
  );
  const explicitlyRequestedKeys = requestedKeys.filter(
    (key) => !mandatoryKeys.has(key),
  );
  assertQuoteFieldSelectionAllowed(
    state.role,
    quote,
    explicitlyRequestedKeys,
    state.catalog.quoteFieldDefinitions,
    state,
  );
  const fields = state.catalog.quoteFieldDefinitions;
  const selectableFields = selectablePool.flatMap((entry) => {
    const field = configured.get(entry.fieldKey);
    return field ? [{ ...field, label: entry.displayLabel }] : [];
  });
  const selectedKeys = normalizeFieldSelection(
    [...mandatoryKeys, ...requestedKeys],
    selectableFields,
  );
  const selectedSnapshot = selectedKeys.flatMap((key) => {
    const field = selectableFields.find((item) => item.key === key);
    const entry = selectablePool.find((item) => item.fieldKey === key);
    return field && entry
      ? [
          {
            ...toQuoteColumnSnapshot(field, entry.placement),
            fieldKey: field.governance.fieldKey,
            definitionVersion: field.governance.definitionVersion,
            formulaVersion: field.governance.formulaVersion,
          },
        ]
      : [];
  });
  const preservedSelections = quoteFieldSelections(quote).filter(
    (selection) =>
      !mandatoryKeys.has(selection.fieldKey) &&
      !canSelectGovernedQuoteField(
        state.role,
        quote,
        selection.fieldKey,
        fields,
        state,
      ),
  );
  const fieldSelections = [...preservedSelections];
  for (const fieldKey of selectedKeys) {
    if (fieldSelections.some((selection) => selection.fieldKey === fieldKey))
      continue;
    const poolEntry = selectablePool.find(
      (entry) => entry.fieldKey === fieldKey,
    );
    if (!poolEntry) throw new Error(`字段 ${fieldKey} 不在报价字段池`);
    fieldSelections.push({
      fieldKey,
      placement: poolEntry.placement,
    });
  }
  const quoteLineKeys = fieldSelections
    .filter((selection) => selection.placement === 'quote-line')
    .map((selection) => selection.fieldKey);
  const sourceSnapshots = captureQuoteSourceFieldSnapshots(
    state,
    quote,
    fieldSelections,
    fieldValuePort,
  );
  return updateQuote(state, quoteId, (currentQuote) => ({
    ...currentQuote,
    items: sourceSnapshots.items,
    sourceFieldValueSnapshot: sourceSnapshots.sourceFieldValueSnapshot,
    fieldSelections,
    customColumns: mergeRawQuoteFieldKeys(
      currentQuote.customColumns,
      quoteLineKeys,
      state.role,
      currentQuote,
      fields,
      state,
    ),
    customColumnSnapshot: mergeRawQuoteColumnSnapshot(
      currentQuote.customColumnSnapshot,
      selectedSnapshot,
      state.role,
      currentQuote,
      fields,
      state,
    ),
  }));
}

export function quoteFieldDisplayLabel(
  field: Pick<QuoteFieldDefinitionRecord, 'label' | 'note'>,
): string {
  return field.note ? `${field.label}（${field.note}）` : field.label;
}

export function normalizeFieldSelection(
  keys: string[],
  fields: QuoteFieldDefinitionRecord[],
): string[] {
  const requested = new Set(keys);
  return fields
    .filter(
      (field) =>
        field.enabled &&
        field.governance.classification === 'ordinary' &&
        isQuoteFieldAvailable(field.key, fields) &&
        requested.has(field.key),
    )
    .map((field) => field.key);
}

export function toggleFieldSelection(
  keys: string[],
  key: string,
  fields: QuoteFieldDefinitionRecord[],
): string[] {
  const selected = new Set(normalizeFieldSelection(keys, fields));
  if (
    !fields.some(
      (field) =>
        field.enabled &&
        field.governance.classification === 'ordinary' &&
        isQuoteFieldAvailable(field.key, fields) &&
        field.key === key,
    )
  ) {
    return [...selected];
  }
  if (selected.has(key)) selected.delete(key);
  else selected.add(key);
  return normalizeFieldSelection([...selected], fields);
}

export function selectAllFields(
  fields: QuoteFieldDefinitionRecord[],
): string[] {
  return fields
    .filter(
      (field) =>
        field.enabled &&
        field.governance.classification === 'ordinary' &&
        isQuoteFieldAvailable(field.key, fields),
    )
    .map((field) => field.key);
}

export function selectDefaultFields(
  fields: QuoteFieldDefinitionRecord[],
): string[] {
  return fields
    .filter(
      (field) =>
        field.enabled &&
        field.governance.classification === 'ordinary' &&
        field.defaultVisible &&
        isQuoteFieldAvailable(field.key, fields),
    )
    .map((field) => field.key);
}

export function filterFieldsByChineseLabel(
  fields: QuoteFieldDefinitionRecord[],
  query: string,
): QuoteFieldDefinitionRecord[] {
  const keyword = query.trim().toLocaleLowerCase('zh-CN');
  if (!keyword) return fields;
  return fields.filter((field) =>
    quoteFieldDisplayLabel(field).toLocaleLowerCase('zh-CN').includes(keyword),
  );
}
